import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import ItemBuilder from '../../features/item-builder/ItemBuilder';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';
import { SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import TaskLoadingMetricsDisplay from '../TaskLoadingMetricsDisplay';
import BannerCard from './BannerCard';
import useConnectionAlerts from './hooks/useConnectionAlerts.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  middlePane: {
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Anchor for the absolutely-positioned WebSocket connection alerts.
    position: 'relative',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
  // Single center card: Current Step section pinned at the top, option groups
  // scrolling below it. Flush like the side panes — no border/radius; the pane
  // dividers supply the only separation.
  centerCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    overflow: 'hidden',
  },
  optionScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 0,
    // ItemBuilder keeps its original spacing(7) top padding (it makes room for
    // the Banner overlay in the OLD layout). Here BannerCard is a separate flex
    // child above, so drop that top gap to keep the option groups flush.
    '& #itemBuilder': {
      paddingTop: 0,
    },
  },
}));

interface CenterPaneProps {
  setCartItemRef: SetCartItemRef;
}

/**
 * MIDDLE pane of the HITL console: task-loading metrics readout, the WebSocket
 * connection alerts, and the single center card (current-step banner pinned at
 * the top + the scrolling option groups from ItemBuilder).
 */
const CenterPane = ({ setCartItemRef }: CenterPaneProps) => {
  const classes = useStyles();
  const { lastSentMetrics } = useTaskLoadingMetricsContext();
  const {
    eventWSConnectionMessage,
    audioWSConnectionMessage,
    eventWSConnectionSuccess,
    audioWSConnectionSuccess,
    showEventAlert,
    handleCloseEventAlert,
    showAudioAlert,
    handleCloseAudioAlert,
  } = useConnectionAlerts();

  return (
    <div className={classes.middlePane}>
      <TaskLoadingMetricsDisplay metrics={lastSentMetrics} />
      {showEventAlert && eventWSConnectionMessage && (
        <Alert
          variant="filled"
          severity={eventWSConnectionSuccess ? 'success' : 'error'}
          className={classes.alertClass}
          onClose={eventWSConnectionSuccess ? handleCloseEventAlert : undefined}
        >
          Event WebSocket: {eventWSConnectionMessage}
        </Alert>
      )}
      {showAudioAlert && audioWSConnectionMessage && (
        <Alert
          variant="filled"
          severity={audioWSConnectionSuccess ? 'success' : 'error'}
          className={classes.alertClass}
          onClose={audioWSConnectionSuccess ? handleCloseAudioAlert : undefined}
          sx={{
            top:
              showEventAlert && eventWSConnectionMessage ? '72px' : undefined,
          }}
        >
          Audio WebSocket: {audioWSConnectionMessage}
        </Alert>
      )}
      <div className={classes.centerCard}>
        <BannerCard />
        <div className={classes.optionScroll}>
          <ItemBuilder setCartItemRef={setCartItemRef} />
        </div>
      </div>
    </div>
  );
};

export default CenterPane;
